// Classifies an API failure as "the server is unreachable" (outage / network
// down / gateway error) vs a normal application or auth error. This is THE
// decision point for offline mode: only server-down-class failures may flip
// the app into offline mode, and they must NEVER be treated as an auth
// rejection (spec §1 — the 2026-08 outage logged users out because any login
// failure called logout()).
//
// 500 is intentionally NOT server-down: the backend returns 500 for app-level
// failures while perfectly healthy. Outages present as connection failures or
// 502/503/504 from the proxy layer.
//
// DELIBERATE TRADE-OFF (do not "fix" by adding 500 here). The exclusion cuts
// both ways and we accept the losing side:
//   - Kept out: a healthy server answering 500 on one broken process would
//     otherwise drag the ENTIRE app into offline mode and hide every working
//     feature behind a false outage banner. 500 is by far the most common
//     application-error status here, so this misclassification would be
//     routine, not rare.
//   - The cost: an outage that presents as 500 (rather than a connection
//     failure or a 502/503/504 from the proxy) is NOT recognized as an outage.
//     Such a session falls back to the pre-offline-mode behavior — the legacy
//     lockout the spec §1 describes: login fails, credentials are treated as
//     rejected, no offline session is offered. Widening the classifier is the
//     wrong lever; if a real outage ever surfaces as 500, fix it at the proxy
//     so it presents as 502/503/504.

const SERVER_DOWN_CODES = new Set(["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"]);
const SERVER_DOWN_STATUSES = new Set([502, 503, 504]);
const AUTH_STATUSES = new Set([401, 403]);

// api.js wraps axios errors: new Error(message, { cause: axiosError }).
// Accept both shapes so callers don't care which layer threw.
const unwrap = (error) => (error.cause && typeof error.cause === "object" ? error.cause : error);

export const isServerDownError = (error) => {
  if (!error || typeof error !== "object") return false;
  const axiosErr = unwrap(error);
  if (axiosErr.code && SERVER_DOWN_CODES.has(axiosErr.code)) return true;
  const status = axiosErr.response?.status;
  return SERVER_DOWN_STATUSES.has(status);
};

// "Your session is not (or no longer) authorized." Distinct from both an
// outage and an application rejection, and it must be treated like the OUTAGE
// for anything that counts failures against a payload:
//
// 401/403 is not a verdict on the request body. The server never evaluated it;
// it declined to look. The session is what is wrong, useAuth owns getting it
// back, and the identical payload replays fine afterwards. A retry counter that
// strikes on this would destroy perfectly good queued work over a token that
// expired in a drawer overnight — and a recovery path that revalidates AFTER
// announcing itself (useAuth's markServerBack flips serverReachable before it
// awaits checkAuth) makes that a routine ordering, not a rare race.
export const isAuthStatus = (status) => AUTH_STATUSES.has(status);

export const isAuthError = (error) => {
  if (!error || typeof error !== "object") return false;
  return isAuthStatus(unwrap(error).response?.status);
};

// The HTTP status behind a thrown API failure, whichever layer wrapped it.
// `undefined` for a transport failure that never got a response.
export const statusOf = (error) => (error && typeof error === "object" ? unwrap(error).response?.status : undefined);

// Transient BY CONTRACT: the server is telling us to come back, not judging the
// body. 408 Request Timeout, 425 Too Early, 429 Too Many Requests.
const TRANSIENT_STATUSES = new Set([408, 425, 429]);

// Does this status represent THE SERVER'S VERDICT ON THIS PAYLOAD? The one
// question a retry counter is entitled to ask, in one place, because an ad-hoc
// copy of it at the call site is how a resolved 503 and a thrown 503 ended up
// classified differently.
//
// Requires: a real number; >= 400; not auth-class (the session is what is
// wrong, not the body); not server-down-class (an outage evaluated nothing);
// not transient-by-contract.
//
// 500 IS strikeable — deliberately, and only when it is the verdict on the
// write itself. This backend's actual permanent-rejection mode is a server-side
// exception returned as a bare 500 (the known user-edit email-collision and
// rate-limiter-NPE classes), so excluding it would leave the single most likely
// permanently-unreplayable payload nagging forever — recreating the bug the
// registry exists for, with extra steps. The danger it carries (a fleet-wide
// incident answering 500 to every write) is answered where strikes are COUNTED,
// not here: distinct-day strikes plus the batch-incident guard make an incident
// cost at most one strike per calendar day, and usually zero. A failure that
// happens BEFORE the payload is sent never reaches this function at all — it
// carries its own non-strikeable verdict.
export const isStrikeableStatus = (status) =>
  typeof status === "number" &&
  status >= 400 &&
  !AUTH_STATUSES.has(status) &&
  !SERVER_DOWN_STATUSES.has(status) &&
  !TRANSIENT_STATUSES.has(status);
